/** Typed REST client for the StarGrid report engine. Deleting a report goes through `/pnl-reports`. */

import { getJson, sendJson } from 'src/components/star-grid/services/starGridHttp';
import { CATALOG_MAX_AGE_MS, REPORT_ENGINE_PREFIX } from 'src/components/star-grid/starGridConstants';
import {
  DrillDimension,
  DrillRequest,
  ImportedScreen,
  PreviewRequest,
  QueryResponse,
  ReportQueryRequest,
  ReportSpec,
  ReportSummary,
  TemplateScreenCatalog
} from 'src/components/star-grid/api/reportEngineTypes';

const reportPath = (reportId: string, suffix = ''): string => `${REPORT_ENGINE_PREFIX}/reports/${encodeURIComponent(reportId)}${suffix}`;

/** The templates plus the deck screens the Template control offers, in one round trip. */
export function getStudioCatalog(signal?: AbortSignal): Promise<TemplateScreenCatalog> {
  return getJson<TemplateScreenCatalog>(`${REPORT_ENGINE_PREFIX}/studio-catalog`, signal, CATALOG_MAX_AGE_MS);
}

/** One deck screen as a spec the studio can load. */
export function importScreen(screenId: string, signal?: AbortSignal): Promise<ImportedScreen> {
  return getJson<ImportedScreen>(`${REPORT_ENGINE_PREFIX}/screens/${encodeURIComponent(screenId)}/import`, signal, CATALOG_MAX_AGE_MS);
}

export function listReports(signal?: AbortSignal): Promise<ReportSummary[]> {
  return getJson<ReportSummary[]>(`${REPORT_ENGINE_PREFIX}/reports`, signal);
}

/** One report's full spec: the studio's edit entry point and the viewer's. */
export function getReport(reportId: string, signal?: AbortSignal): Promise<ReportSpec> {
  return getJson<ReportSpec>(reportPath(reportId), signal);
}

/** Returns the stored spec, now carrying its server-assigned `id`. */
export function createReport(spec: ReportSpec, signal?: AbortSignal): Promise<ReportSpec> {
  return sendJson<ReportSpec>('POST', `${REPORT_ENGINE_PREFIX}/reports`, spec, signal);
}

/** Replaces a report's spec wholesale. */
export function updateReport(reportId: string, spec: ReportSpec, signal?: AbortSignal): Promise<ReportSpec> {
  return sendJson<ReportSpec>('PUT', reportPath(reportId), spec, signal);
}

export function queryReport(reportId: string, request: ReportQueryRequest, signal?: AbortSignal): Promise<QueryResponse> {
  return sendJson<QueryResponse>('POST', reportPath(reportId, '/query'), request, signal);
}

/** StarRocks' plans for the query a request would run; planned only, nothing is scanned. */
export async function explainReport(reportId: string, request: ReportQueryRequest, signal?: AbortSignal): Promise<readonly string[]> {
  const response = await sendJson<QueryResponse>('POST', reportPath(reportId, '/query?explain=true'), request, signal);
  return response.plans ?? [];
}

/**
 * The dimensions a row can be broken down by. Separate from the query: the menu is the same for every
 * row, so computing it per query would put a `pnl_paths` round trip on the report's critical path.
 */
export function listDrillDimensions(reportId: string, deckType?: string, signal?: AbortSignal): Promise<DrillDimension[]> {
  const query = deckType ? `?deck_type=${encodeURIComponent(deckType)}` : '';
  return getJson<DrillDimension[]>(reportPath(reportId, `/drill-dimensions${query}`), signal);
}

/** One row's breakdown by a second dimension, as rows to splice in beneath it. */
export function drillReport(reportId: string, request: DrillRequest, signal?: AbortSignal): Promise<QueryResponse> {
  return sendJson<QueryResponse>('POST', reportPath(reportId, '/drill'), request, signal);
}

/** Renders an unsaved spec, so the studio can preview before the report exists. */
export function previewReport(request: PreviewRequest, signal?: AbortSignal): Promise<QueryResponse> {
  return sendJson<QueryResponse>('POST', `${REPORT_ENGINE_PREFIX}/reports/preview`, request, signal);
}
